// Centralized subscription state management
// Native: RevenueCat as source of truth. Web: Firestore (subscription.stripeActive) updated by Stripe webhooks.
import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { doc, setDoc, getDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { firestore, auth } from "./firebaseConfig";
import { useAuth } from "../contexts/AuthContext";
import { isNativeWebView, rcSync, onNativeMessage } from "../nativeBridge.ts";
import { getCachedUserMeta, setCachedUserMeta } from "./firebaseService";
import DevSubscriptionOverrideContext from "../contexts/DevSubscriptionOverrideContext";

const RC_ENTITLEMENT_ID = "tidle_plus";

/**
 * Hook for subscription status and management
 * - Native: RevenueCat entitlement + sync to Firestore; listens for native messages
 * - Web: Firestore subscription.stripeActive (set by Stripe webhooks); real-time listener
 */
export function useSubscriptionStatus() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  const [isPro, setIsPro] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track last sync to prevent excessive Firestore writes
  const lastSyncRef = useRef(0);
  const SYNC_DEBOUNCE_MS = 5000; // Only sync to Firestore every 5 seconds max

  // Sync subscription status to Firestore (non-authoritative cache) — native only
  const syncToFirestore = useCallback(
    async (active, packageId = null) => {
      if (!uid || !auth.currentUser) return;

      const now = Date.now();
      if (now - lastSyncRef.current < SYNC_DEBOUNCE_MS) return;

      try {
        lastSyncRef.current = now;
        const userRef = doc(firestore, "users", uid);

        // Use setDoc with merge to preserve other user data (e.g. stripeActive)
        await setDoc(
          userRef,
          {
            subscription: {
              lastSeenEntitlementActive: active,
              lastSeenPackageId: packageId,
              lastSyncedAt: serverTimestamp(),
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (err) {
        console.warn("[SubscriptionService] Failed to sync to Firestore:", err);
        // Non-critical, don't throw
      }
    },
    [uid]
  );

  // Check subscription status: RevenueCat on native, Firestore stripeActive on web
  const checkStatus = useCallback(async () => {
    if (isNativeWebView()) {
      try {
        setIsLoading(true);
        setError(null);
        const res = await rcSync();
        if (res && res.ok) {
          const active = !!res.active;
          setIsPro(active);
          await syncToFirestore(active, res.packageId || null);
        } else {
          setIsPro(false);
          await syncToFirestore(false);
        }
      } catch (err) {
        console.error("[SubscriptionService] Error checking status:", err);
        setError(err.message || "Failed to check subscription status");
        setIsPro(false);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Web: read from Firestore (Stripe webhook keeps subscription.stripeActive up to date)
    if (!uid) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const userRef = doc(firestore, "users", uid);
      const snap = await getDoc(userRef);
      const sub = snap.data()?.subscription;
      setIsPro(!!sub?.stripeActive);
      setStripeCustomerId(sub?.stripeCustomerId ?? null);
    } catch (err) {
      console.error("[SubscriptionService] Error checking status (web):", err);
      setError(err.message || "Failed to check subscription status");
      setIsPro(false);
    } finally {
      setIsLoading(false);
    }
  }, [syncToFirestore, uid]);

  // Listen for real-time updates from native (CustomerInfo updates)
  useEffect(() => {
    if (!isNativeWebView()) return;

    const unsubscribe = onNativeMessage((msg) => {
      if (msg.type === "RC_STATUS") {
        const active = !!msg.active;
        setIsPro(active);
        syncToFirestore(active, null);
      }
    });

    return unsubscribe;
  }, [syncToFirestore]);

  // Web: real-time Firestore listener for subscription.stripeActive
  useEffect(() => {
    if (isNativeWebView() || !uid) return;

    const userRef = doc(firestore, "users", uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snap) => {
        const sub = snap.data()?.subscription;
        setIsPro(!!sub?.stripeActive);
        setStripeCustomerId(sub?.stripeCustomerId ?? null);
      },
      (err) => {
        console.warn("[SubscriptionService] Firestore snapshot error:", err);
      }
    );
    return () => unsubscribe();
  }, [uid]);

  // Initial check on mount and when user changes
  useEffect(() => {
    if (!uid) {
      setIsPro(false);
      setIsLoading(false);
      return;
    }

    checkStatus();
  }, [uid, checkStatus]);

  // Dev override: in development, allow forcing Pro/Free for testing gates
  const devOverride = useContext(DevSubscriptionOverrideContext);
  const effectiveIsPro =
    process.env.NODE_ENV === "development" &&
    devOverride?.devProOverride !== null &&
    devOverride?.devProOverride !== undefined
      ? devOverride.devProOverride
      : isPro;

  return {
    isPro: effectiveIsPro,
    stripeCustomerId,
    isLoading,
    error,
    refresh: checkStatus,
  };
}

/**
 * Get user's preferred plan (optimization #2: uses cache, falls back to Firestore)
 */
export async function getUserPreferredPlan(uid) {
  if (!uid) return "annual"; // Default

  // Try cache first (optimization #2)
  const cached = getCachedUserMeta(uid);
  if (cached?.subscription?.preferredPlan) {
    const preferred = cached.subscription.preferredPlan;
    if (preferred === "monthly" || preferred === "annual") {
      return preferred;
    }
  }

  // Cache miss - fallback to Firestore read
  try {
    const userRef = doc(firestore, "users", uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const data = userSnap.data();
      const preferred = data?.subscription?.preferredPlan;
      
      // Cache the data for future reads (optimization #2)
      const cached = getCachedUserMeta(uid) || {};
      setCachedUserMeta(uid, {
        ...cached,
        subscription: {
          ...cached.subscription,
          preferredPlan: preferred,
        },
      });
      
      if (preferred === "monthly" || preferred === "annual") {
        return preferred;
      }
    }
  } catch (err) {
    console.warn("[SubscriptionService] Failed to get preferred plan:", err);
  }

  return "annual"; // Default to annual
}

/**
 * Save user's preferred plan to Firestore (optimization #2: updates cache immediately)
 */
export async function saveUserPreferredPlan(uid, plan) {
  if (!uid || (plan !== "monthly" && plan !== "annual")) return;

  try {
    const userRef = doc(firestore, "users", uid);
    await setDoc(
      userRef,
      {
        subscription: {
          preferredPlan: plan,
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    
    // Update cache immediately (optimization #2)
    const cached = getCachedUserMeta(uid) || {};
    setCachedUserMeta(uid, {
      ...cached,
      subscription: {
        ...cached.subscription,
        preferredPlan: plan,
      },
    });
  } catch (err) {
    console.error("[SubscriptionService] Failed to save preferred plan:", err);
    throw err;
  }
}

/**
 * Save purchase metadata to Firestore after successful purchase
 */
export async function savePurchaseMetadata(uid, packageId) {
  if (!uid) return;

  try {
    const userRef = doc(firestore, "users", uid);
    await setDoc(
      userRef,
      {
        subscription: {
          lastPurchasePackageId: packageId,
          lastPurchaseAt: serverTimestamp(),
          lastSeenEntitlementActive: true,
          lastSyncedAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn("[SubscriptionService] Failed to save purchase metadata:", err);
    // Non-critical
  }
}
